import {inject, Injectable} from '@angular/core';
import {MenuItem} from "../../views/layout/sidebar/menu.model";
import {CoreConfig} from "../core.config";
import {Profile} from "../models/profile.model";
import {HttpClient} from "@angular/common/http";
import {first, map, switchMap} from "rxjs/operators";
import {DominoArrayResponse, DominoMenuItem, DominoMenuItemChildren} from "../models/domino-response.model";
import {BehaviorSubject, Observable, ReplaySubject, tap} from "rxjs";
import {ProfileStateService} from "../states/profile-state.service";

/**
 * Service to load and provide menu items based on user profile and roles
 * Centralized menu state management that automatically loads menu when profile changes
 * Link to change dev menu: https://symdev04.improvento.com/cust/regionhovedstaden/menu/ban-Menu.nsf/index.html?openpage
 */
@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private coreConfig = inject(CoreConfig);
  private http = inject(HttpClient);
  private profileStateService = inject(ProfileStateService);
  private dominoMenuItems$ = new ReplaySubject<DominoMenuItem[]>(1);
  private mappedMenuItemsSubject = new BehaviorSubject<MenuItem[]>([]);

  /**
   * Observable of mapped menu items ready for UI consumption
   * Automatically updates when profile changes or menu is refreshed
   */
  public readonly menuItems$ = this.mappedMenuItemsSubject.asObservable();

  constructor() {
    // Auto-load menu when profile changes
    this.profileStateService.profile$.pipe(
      switchMap(profile => this.getMenuItems(profile)),
      tap(menuItems => this.mappedMenuItemsSubject.next(menuItems)),
      tap(menuItems => console.log("Menu items updated:", menuItems)),
    ).subscribe();
  }


  getMenuItems(profile: Profile | null): Observable<MenuItem[]> {
    const profileRoles = profile?.roles || ['anonymous'];
    return this.loadMenuItems().pipe(
      map((result) => this.mapToMenuItems(result, profileRoles)),
    );
  }

  getMenuItemTitleByUrl(url: string): Observable<string | null> {
    return this.dominoMenuItems$.pipe(
      map((items) => {
        const flattenedItems = this.flattenDominoMenuItems(items);
        //find the most matching item
        let foundItem = flattenedItems.find(item => item.url === url)
          ?? flattenedItems.find(item => item.url && url.startsWith(item.url))
          ?? undefined;

        return foundItem ? foundItem.text : null;
      })
    );
  }

  /**
   * Get the default URL from menu configuration
   * Ensures menu is loaded before returning
   */
  getDefaultUrl(): Observable<string> {
    return this.dominoMenuItems$.pipe(
      first(),
      map((items) => {
        const flattenedItems = this.flattenDominoMenuItems(items);
        const defaultItem = flattenedItems.find(item => item?.default);
        return defaultItem?.url || '/';
      }),
    );
  }

  private loadMenuItems(): Observable<DominoMenuItem[]> {
    return this.http.get<DominoArrayResponse<DominoMenuItem>>(`${this.coreConfig.api.baseUrl}/${this.coreConfig.dominoConfig.menudatabase}/GetMenu?OpenAgent`).pipe(
      tap(result => this.dominoMenuItems$.next(result.data)),
      map(result => result.data)
    );
  }

  private flattenDominoMenuItems(items: DominoMenuItem[]): DominoMenuItem[] {
    return items.reduce((acc, item) => {
      acc.push(item);
      if (item.children) {
        acc.push(...this.flattenDominoMenuItems(item.children));
      }
      return acc;
    }, [] as DominoMenuItem[]);
  }


  private mapToMenuItems(items: DominoMenuItem[], profileRoles: string[]): MenuItem[] {
    return items
      .filter(item => this.anyRoleMatch(profileRoles, this.getRolesForMenuItem(item)))
      .map(item => {
        const menuItem = {
          label: item.text,
          iconClass: item.badgeurl,
          link: item.url ?? '',
          openInNewWindow: item.openInNewWindow ?? false,
          isExternalLink: String(item.url).startsWith('http'),
        } as MenuItem;
        menuItem.subItems = item.children ? this.mapChildrenToMenuItems(item.children, profileRoles) : [];
        return menuItem;
      });
  }

  private mapChildrenToMenuItems(children: DominoMenuItemChildren[], profileRoles: string[]): MenuItem[] {
    return children.filter(item => this.anyRoleMatch(profileRoles, this.getRolesForMenuItem(item)))
      .map(item => {
        return {
          label: item.text,
          iconClass: item.badgeurl,
          link: item.url,
          openInNewWindow: item.openInNewWindow ?? false,
          isExternalLink: String(item.url).startsWith('http'),
        } as MenuItem;
      });
  }

  private anyRoleMatch(profileRoles: string[], menuItemRoles: string[]) {
    profileRoles = profileRoles.map(role => role.toLowerCase());
    menuItemRoles = menuItemRoles.map(role => role.toLowerCase());

    return menuItemRoles.some(role => profileRoles.includes(role));
  }

  private getRolesForMenuItem(item: DominoMenuItem | DominoMenuItemChildren): string[] {
    // if  item.access.roles is a string, convert it to an array. item.access can also be empty
    if (Array.isArray(item?.access?.roles)) {
      return item?.access?.roles as string[];
    }

    if (typeof item?.access?.roles === 'string') {
      return [item?.access?.roles] as string[];
    }

    return [];
  }
}

